import { useQueryClient } from '@tanstack/react-query'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router'

import type { UserResponse } from '@/api/responses'
import { AppErrorPage } from '@/components/layout/AppErrorPage'
import { AppShellLayout } from '@/components/layout/AppShellLayout'
import { NotFoundPage } from '@/components/layout/NotFoundPage'
import { AgentsPage } from '@/features/agents/AgentsPage'
import { AnalyticsPage } from '@/features/analytics/AnalyticsPage'
import { AuditPage } from '@/features/audit/AuditPage'
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage'
import { LoginPage } from '@/features/auth/LoginPage'
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage'
import { SignupPage } from '@/features/auth/SignupPage'
import {
  decideAppShellAccess,
  decideAuthPageAccess,
  decideOnboardingAccess,
} from '@/features/auth/guards'
import { sessionQueryKey } from '@/features/auth/session'
import { ChannelsPage } from '@/features/channels/ChannelsPage'
import { DeskPage } from '@/features/conversations/DeskPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { AcceptInvitePage } from '@/features/invitations/AcceptInvitePage'
import { consumePendingInvite } from '@/features/invitations/pendingInvite'
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage'
import { KnowledgePage } from '@/features/knowledge/KnowledgePage'
import { LandingPage } from '@/features/landing/LandingPage'
import { OnboardingWizardPage } from '@/features/onboarding/OnboardingWizardPage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { PendenciasPage } from '@/features/triage/PendenciasPage'
import { listMyTenants } from '@/features/tenants/api'
import { tenantsQueryKey } from '@/features/tenants/queries'
import { BillingPage } from '@/features/billing/BillingPage'
import { TeamPage } from '@/features/team/TeamPage'
import { WebChatPage } from '@/features/webchat/WebChatPage'

import { applyGuardDecision, loadSession, loadTenants } from './routeGuards'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
})

async function guardAuthPage(): Promise<void> {
  const user = await loadSession()
  const tenants = user !== null ? await loadTenants() : []
  applyGuardDecision(decideAuthPageAccess(user, tenants))
}

async function guardOnboarding(): Promise<void> {
  const user = await loadSession()
  const tenants = user !== null ? await loadTenants() : []
  applyGuardDecision(decideOnboardingAccess(user, tenants))
}

/**
 * Apos login/cadastro, atualiza o cache de sessao/tenants e navega para onde o usuario deve
 * ir (onboarding sem organizacao, dashboard da primeira organizacao caso contrario) — a mesma
 * regra usada pelos guards de rota (features/auth/guards.ts).
 */
function useAuthSuccessRedirect() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return async (user: UserResponse): Promise<void> => {
    queryClient.setQueryData(sessionQueryKey, user)
    const tenants = await listMyTenants()
    queryClient.setQueryData(tenantsQueryKey, tenants)

    // Se o usuario chegou por um link de convite antes de logar, volta ao aceite em vez do destino padrao.
    const pendingInvite = consumePendingInvite()
    if (pendingInvite !== null) {
      await navigate({ to: '/invite/$token', params: { token: pendingInvite } })
      return
    }

    const decision = decideAuthPageAccess(user, tenants)

    if (decision.kind !== 'redirect') {
      return
    }

    if (decision.to === '/app/$tenantSlug/dashboard') {
      await navigate({ to: decision.to, params: decision.params })
      return
    }

    await navigate({ to: decision.to })
  }
}

function LoginRouteComponent() {
  const navigate = useNavigate()
  const redirectAfterAuth = useAuthSuccessRedirect()

  return (
    <LoginPage
      onSuccess={(user) => {
        void redirectAfterAuth(user)
      }}
      onNavigateToSignup={() => {
        void navigate({ to: '/signup' })
      }}
      onNavigateToForgotPassword={() => {
        void navigate({ to: '/esqueci-senha' })
      }}
    />
  )
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: guardAuthPage,
  component: LoginRouteComponent,
})

// Recuperacao de senha: rotas publicas (o usuario esqueceu a senha, pode nao ter sessao). O link do
// e-mail aponta para /redefinir-senha?uid=...&token=... — os parametros de busca sao validados abaixo.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/esqueci-senha',
  component: function ForgotPasswordRouteComponent() {
    const navigate = useNavigate()
    return (
      <ForgotPasswordPage
        onNavigateToLogin={() => {
          void navigate({ to: '/login' })
        }}
      />
    )
  },
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redefinir-senha',
  validateSearch: (search: Record<string, unknown>): { uid: string; token: string } => ({
    uid: typeof search.uid === 'string' ? search.uid : '',
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: function ResetPasswordRouteComponent() {
    const navigate = useNavigate()
    const { uid, token } = resetPasswordRoute.useSearch()
    return (
      <ResetPasswordPage
        uid={uid}
        token={token}
        onNavigateToLogin={() => {
          void navigate({ to: '/login' })
        }}
        onRequestNewLink={() => {
          void navigate({ to: '/esqueci-senha' })
        }}
      />
    )
  },
})

function SignupRouteComponent() {
  const navigate = useNavigate()
  const redirectAfterAuth = useAuthSuccessRedirect()

  return (
    <SignupPage
      onSuccess={(user) => {
        void redirectAfterAuth(user)
      }}
      onNavigateToLogin={() => {
        void navigate({ to: '/login' })
      }}
    />
  )
}

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  beforeLoad: guardAuthPage,
  component: SignupRouteComponent,
})

// Aceite de convite: rota publica (o convidado pode nao ter conta/sessao ainda). A propria pagina
// decide: sem sessao -> guarda o token e vai ao login (que volta aqui); com sessao -> aceita.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: function InviteRouteComponent() {
    const { token } = inviteRoute.useParams()
    return <AcceptInvitePage token={token} />
  },
})

function OnboardingRouteComponent() {
  const navigate = useNavigate()

  return (
    <OnboardingWizardPage
      onGoLive={(tenant) => {
        void navigate({ to: '/app/$tenantSlug/dashboard', params: { tenantSlug: tenant.slug } })
      }}
    />
  )
}

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: guardOnboarding,
  component: OnboardingRouteComponent,
})

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/$tenantSlug',
  beforeLoad: async ({ params }) => {
    const user = await loadSession()
    const tenants = user !== null ? await loadTenants() : []
    applyGuardDecision(decideAppShellAccess(user, tenants, params.tenantSlug))
  },
  component: AppShellLayout,
})

const appIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- redirect() retorna Response, padrao do TanStack Router
    throw redirect({ to: '/app/$tenantSlug/dashboard', params: { tenantSlug: params.tenantSlug } })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
})

const deskRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/desk',
  // Pendências abre uma conversa específica: /desk?conversation=<seq>.
  validateSearch: (search: Record<string, unknown>): { conversation?: number } => {
    const raw = search.conversation
    const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN
    return Number.isInteger(value) && value > 0 ? { conversation: value } : {}
  },
  component: DeskPage,
})

const pendenciasRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pendencias',
  component: PendenciasPage,
})

const agentsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/agents',
  component: AgentsPage,
})

const knowledgeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/knowledge',
  component: KnowledgePage,
})

const channelsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/channels',
  component: ChannelsPage,
})

const webchatRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/webchat',
  component: WebChatPage,
})

const integrationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/integrations',
  component: IntegrationsPage,
})

const analyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/analytics',
  component: AnalyticsPage,
})

const auditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/audit',
  component: AuditPage,
})

const teamRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/team',
  component: TeamPage,
})

const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/billing',
  component: BillingPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: SettingsPage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  inviteRoute,
  onboardingRoute,
  appLayoutRoute.addChildren([
    appIndexRoute,
    dashboardRoute,
    deskRoute,
    pendenciasRoute,
    agentsRoute,
    knowledgeRoute,
    channelsRoute,
    webchatRoute,
    integrationsRoute,
    analyticsRoute,
    auditRoute,
    teamRoute,
    billingRoute,
    settingsRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  // Telas graciosas: erro de render de rota e URL desconhecida nunca ficam em branco.
  defaultErrorComponent: AppErrorPage,
  defaultNotFoundComponent: NotFoundPage,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
